export class Tabs {
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    this.handleTabs();
  }

  private handleTabs() {
    // adapted from https://codepen.io/heydon/pen/veeaEa/
    // Get relevant elements and collections
    const tablist = this.element.querySelector("ul") as HTMLUListElement;
    const tabs = tablist.querySelectorAll(
      ".tabs__tab-trigger"
    ) as NodeListOf<HTMLButtonElement>;
    const panels = this.element.querySelectorAll(
      ".tabs__tab-content"
    ) as NodeListOf<HTMLElement>;

    // The tab switching function
    const switchTab = (oldTab: any, newTab: any) => {
      newTab.focus();
      // Make the active tab focusable by the user (Tab key)
      newTab.removeAttribute("tabindex");
      // Set the selected state
      newTab.setAttribute("aria-selected", "true");
      oldTab.removeAttribute("aria-selected");
      oldTab.setAttribute("tabindex", "-1");
      // Get the indices of the new and old tabs to find the correct
      // tab panels to show and hide
      let index = Array.prototype.indexOf.call(tabs, newTab);
      let oldIndex = Array.prototype.indexOf.call(tabs, oldTab);
      panels[oldIndex].hidden = true;
      panels[index].hidden = false;
    };

    // Add the tablist role to the first <ul> in the .tabbed container
    tablist.setAttribute("role", "tablist");

    // Add semantics are remove user focusability for each tab
    Array.prototype.forEach.call(tabs, (tab: any, i: any) => {
      tab.setAttribute("role", "tab");
      let randomString = Math.random().toString(36).substring(2, 8);
      tab.setAttribute("id", `tab${i + 1}-${randomString}`);
      tab.setAttribute("tabindex", "-1");
      tab.parentNode.setAttribute("role", "presentation");

      // Handle clicking of tabs for mouse users
      tab.addEventListener("click", (e: any) => {
        e.preventDefault();
        let currentTab = tablist.querySelector("[aria-selected]");
        if (e.currentTarget !== currentTab) {
          switchTab(currentTab, e.currentTarget);
        }
      });

      // Handle keydown events for keyboard users
      tab.addEventListener("keydown", (e: any) => {
        // Get the index of the current tab in the tabs node list
        let index = Array.prototype.indexOf.call(tabs, e.currentTarget);
        // Work out which key the user is pressing and
        // Calculate the new tab's index where appropriate
        let dir: number | "down" | null =
          e.which === 37
            ? index - 1 // Left arrow
            : e.which === 39
            ? index + 1 // Right arrow
            : e.which === 40
            ? "down"
            : null; // Down arrow
        if (dir !== null) {
          e.preventDefault();
          // If the down key is pressed, move focus to the open panel,
          // otherwise switch to the adjacent tab
          if (dir === "down") {
            panels[index].focus(); // Use the current index to focus the corresponding panel
          } else {
            // Ensure dir is a valid index before accessing tabs
            if (typeof dir === "number" && dir >= 0 && dir < tabs.length) {
              switchTab(e.currentTarget, tabs[dir]);
            }
          }
        }
      });
    });

    // Add tab panel semantics and hide them all
    Array.prototype.forEach.call(panels, (panel: any, i: any) => {
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("tabindex", "-1");
      panel.setAttribute("aria-labelledby", tabs[i].id);
      panel.hidden = true;
    });

    // Initially activate the first tab and reveal the first tab panel
    tabs[0].removeAttribute("tabindex");
    tabs[0].setAttribute("aria-selected", "true");
    panels[0].hidden = false;
  }
}

export default function tabsInit() {
  const tabs = document.querySelectorAll(".tabs") as NodeListOf<HTMLElement>;
  for (let i = 0; i < tabs.length; i++) {
    new Tabs(tabs[i]);
  }
}
